import { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadStripe,
  type PaymentIntent,
  type PaymentRequest,
  type PaymentRequestPaymentMethodEvent,
  type Stripe,
  type StripeElements,
  type StripePaymentElement,
} from '@stripe/stripe-js';
import { apiStore } from '@/stores/ApiStore';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { paymentConfirmationStore } from '@/stores/PaymentConfirmationStore';
import { paywallFlowStore } from '@/stores/PaywallFlowStore';
import { config } from '@/config/env';
import { getErrorMessage } from '@/hooks/usePaymentErrors';
import { isInWebView, sendPaymentSuccess } from '@/hooks/useWebViewMessaging';
import type {
  PaymentAnalyticsPaymentMethod,
  PlanItem,
  SubscriptionConfirmResponse,
} from '@/types/api';
import {
  buildAttConversionClickPayload,
  getStoredAttParams,
  resolveAttributionEmail,
  setConversionClickSent,
  wasConversionClickSent,
} from '@/utils/attParams';
import {
  buildAppsFlyerDeeplinkFromPath,
  buildAppsFlyerRedirectUrl,
} from '@/utils/deeplink';
import {
  consumeAppsFlyerParams,
} from '@/utils/appsFlyerParams';
import { isValidEmailAddress } from '@/utils/email';
import { navigateWithPaymentOverlay } from '@/utils/paymentNavigation';
import { ROUTES } from '@/constants/routes';
import {
  clearPendingWebPayment,
  PENDING_WEB_PAYMENT_STORAGE_KEY,
} from '@/utils/pendingWebPayment';
import { trackSuccessfulPaymentAnalytics } from '@/utils/paymentAnalytics';

interface UseWebTrialCheckoutOptions {
  enabled: boolean;
  plan: PlanItem | null;
  paymentElementContainer: HTMLDivElement | null;
  onPaymentElementFocus?: () => void;
}

interface StripeCheckoutRefs {
  stripe: Stripe;
  elements: StripeElements;
  clientSecret: string;
}

type PendingPaymentStatus = 'awaiting_stripe' | 'finalizing' | 'confirmation_unknown';

interface PendingWebPayment {
  createdAt: number;
  email: string;
  paymentMethod?: PaymentAnalyticsPaymentMethod;
  paymentIntentId?: string;
  planId: string;
  status: PendingPaymentStatus;
}

const PENDING_PAYMENT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx',
  'vnd', 'vuv', 'xaf', 'xof', 'xpf',
]);
const PAYMENT_CONFIRMATION_UNKNOWN_MESSAGE
  = 'Payment may have been processed. Please contact support before trying again.';

function toMinorUnits(amount: string, currency: string): number {
  const numericAmount = Number.parseFloat(amount);
  if (!Number.isFinite(numericAmount)) {
    return 0;
  }

  const multiplier = ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase()) ? 1 : 100;
  return Math.round(numericAmount * multiplier);
}

function hasStripeReturnParams(): boolean {
  return typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('payment_intent_client_secret');
}

function getCleanReturnUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete('payment_intent');
  url.searchParams.delete('payment_intent_client_secret');
  url.searchParams.delete('redirect_status');
  return url.toString();
}

function readPendingPayment(): PendingWebPayment | null {
  try {
    const stored = window.sessionStorage.getItem(PENDING_WEB_PAYMENT_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as Partial<PendingWebPayment>;
    const isValid = typeof parsed.createdAt === 'number'
      && Date.now() - parsed.createdAt <= PENDING_PAYMENT_MAX_AGE_MS
      && typeof parsed.email === 'string'
      && typeof parsed.planId === 'string'
      && (
        parsed.paymentMethod === undefined
        || parsed.paymentMethod === 'card'
        || parsed.paymentMethod === 'apple_pay'
      )
      && (
        parsed.status === 'awaiting_stripe'
        || parsed.status === 'finalizing'
        || parsed.status === 'confirmation_unknown'
      );

    if (!isValid) {
      window.sessionStorage.removeItem(PENDING_WEB_PAYMENT_STORAGE_KEY);
      return null;
    }

    return parsed as PendingWebPayment;
  }
  catch {
    return null;
  }
}

function savePendingPayment(payment: PendingWebPayment): void {
  try {
    window.sessionStorage.setItem(PENDING_WEB_PAYMENT_STORAGE_KEY, JSON.stringify(payment));
  }
  catch {
    // Storage can be unavailable in hardened browser modes; payment still remains usable.
  }
}

function clearPendingPayment(): void {
  clearPendingWebPayment();
}

function createPaymentFlowError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  const source = error && typeof error === 'object'
    ? error as Record<string, unknown>
    : null;
  const message = source && typeof source.message === 'string'
    ? source.message
    : 'Payment processing failed.';
  const paymentError = new Error(message);

  if (source) {
    ['code', 'type', 'decline_code'].forEach((field) => {
      if (typeof source[field] === 'string') {
        Object.assign(paymentError, { [field]: source[field] });
      }
    });
  }

  return paymentError;
}

function getCheckoutErrorMessage(error: unknown): string {
  if (
    error instanceof Error
    && (
      error.message.includes('contact support')
      || error.message.includes('still being verified')
    )
  ) {
    return error.message;
  }

  return getErrorMessage(error);
}

async function decodePaywallMeta(): Promise<string | undefined> {
  const encodedMeta = paywallFlowStore.meta.trim();
  if (!encodedMeta) {
    return undefined;
  }

  try {
    const decodeResult = await apiStore.decodeLink(encodedMeta);
    if (!decodeResult.ok || !decodeResult.data) {
      return undefined;
    }

    const parsed = JSON.parse(decodeResult.data) as unknown;
    if (
      !parsed
      || typeof parsed !== 'object'
      || Array.isArray(parsed)
      || !Object.values(parsed).every((value) => typeof value === 'string')
    ) {
      return undefined;
    }

    return decodeResult.data;
  }
  catch {
    return undefined;
  }
}

async function sendDeferredConversionClick(email: string): Promise<void> {
  const attParams = getStoredAttParams();
  const clickPayload = buildAttConversionClickPayload(attParams, email);

  if (!clickPayload || wasConversionClickSent(clickPayload)) {
    return;
  }

  try {
    await apiStore.conversionClick(clickPayload);
    setConversionClickSent(clickPayload);
  }
  catch (error) {
    console.warn('Unable to send conversion click before subscription confirmation', error);
  }
}

export function useWebTrialCheckout({
  enabled,
  plan,
  paymentElementContainer,
  onPaymentElementFocus,
}: UseWebTrialCheckoutOptions) {
  const [initializing, setInitializing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [error, setError] = useState('');
  const [recovering, setRecovering] = useState(hasStripeReturnParams);
  const [retryKey, setRetryKey] = useState(0);
  const checkoutRefs = useRef<StripeCheckoutRefs | null>(null);
  const paymentRequestRef = useRef<PaymentRequest | null>(null);
  const applePayFlowRef = useRef<Promise<void> | null>(null);
  const paymentElementRef = useRef<StripePaymentElement | null>(null);
  const processingRef = useRef(false);
  const mountedRef = useRef(true);
  const recoveryStartedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      paymentConfirmationStore.stopUnlessNavigating();
    };
  }, []);

  const finishSubscription = useCallback(async (
    paymentIntent: PaymentIntent,
    email: string,
    confirmedPlanId: string,
    paymentMethod: PaymentAnalyticsPaymentMethod,
  ) => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      throw new Error('Email is required to activate your subscription.');
    }

    savePendingPayment({
      createdAt: Date.now(),
      email: normalizedEmail,
      paymentMethod,
      paymentIntentId: paymentIntent.id,
      planId: confirmedPlanId,
      status: 'finalizing',
    });

    paymentConfirmationStore.start();
    try {
      await sendDeferredConversionClick(normalizedEmail);
      const decodedMeta = await decodePaywallMeta();
      const accessTokenBeforeConfirmation = apiStore.accessToken;

      let confirmResult: SubscriptionConfirmResponse;
      try {
        confirmResult = await apiStore.confirmSubscription({
          email: normalizedEmail,
          paymentData: { paymentIntentId: paymentIntent.id },
          meta: decodedMeta,
          planId: confirmedPlanId,
        });
      }
      catch {
        if (
          apiStore.accessToken
          && apiStore.accessToken !== accessTokenBeforeConfirmation
        ) {
          try {
            await subscriptionStore.fetchStatus(true);
            await trackSuccessfulPaymentAnalytics({
              paymentMethod,
              subscription: subscriptionStore.status,
              planId: confirmedPlanId,
            });
          }
          catch {
            // Confirmation succeeded; analytics remains best-effort.
          }
          clearPendingPayment();
          if (!isInWebView()) {
            consumeAppsFlyerParams();
          }
          navigateWithPaymentOverlay(ROUTES.ACCOUNT.SUBSCRIPTION);
          return;
        }

        savePendingPayment({
          createdAt: Date.now(),
          email: normalizedEmail,
          paymentMethod,
          paymentIntentId: paymentIntent.id,
          planId: confirmedPlanId,
          status: 'confirmation_unknown',
        });
        throw new Error(PAYMENT_CONFIRMATION_UNKNOWN_MESSAGE);
      }

      if (!confirmResult.access?.accessToken) {
        savePendingPayment({
          createdAt: Date.now(),
          email: normalizedEmail,
          paymentMethod,
          paymentIntentId: paymentIntent.id,
          planId: confirmedPlanId,
          status: 'confirmation_unknown',
        });
        throw new Error(PAYMENT_CONFIRMATION_UNKNOWN_MESSAGE);
      }

      await trackSuccessfulPaymentAnalytics({
        paymentMethod,
        subscription: confirmResult.status,
        planId: confirmedPlanId,
      });
      sendPaymentSuccess(paymentMethod === 'apple_pay' ? 'applepay' : 'card');

      clearPendingPayment();
      const appsFlyerParams = isInWebView()
        ? null
        : consumeAppsFlyerParams();

      try {
        await subscriptionStore.fetchStatus(true);

        const handoffResult = await apiStore.createHandoff();
        if (handoffResult.ok && handoffResult.token) {
          const deeplink = buildAppsFlyerDeeplinkFromPath(
            window.location.pathname,
            {
              attParams: getStoredAttParams(),
              handoffToken: handoffResult.token,
              paymentMethod,
            },
          );
          const fallbackCampaign = deeplink.paywallVersion
            ? `paywall_${deeplink.paywallVersion}`
            : undefined;
          const redirectUrl = buildAppsFlyerRedirectUrl(
            appsFlyerParams,
            deeplink,
            fallbackCampaign,
          );
          navigateWithPaymentOverlay(redirectUrl);
          return;
        }
      }
      catch {
        // The subscription is confirmed; use the authenticated account as a safe fallback.
      }

      navigateWithPaymentOverlay(ROUTES.ACCOUNT.SUBSCRIPTION);
    }
    finally {
      paymentConfirmationStore.stopUnlessNavigating();
    }
  }, []);

  const confirmStripePayment = useCallback(async (email: string) => {
    if (processingRef.current) {
      return;
    }

    const refs = checkoutRefs.current;
    if (!refs) {
      const notReadyMessage = 'Payment form is still loading. Please try again.';
      setError(notReadyMessage);
      return;
    }

    const normalizedEmail = email.trim();
    if (!isValidEmailAddress(normalizedEmail)) {
      const emailMessage = 'Enter a valid email address to continue.';
      setError(emailMessage);
      return;
    }

    if (!plan) {
      const planMessage = 'Weekly trial plan is not available. Please refresh the page.';
      setError(planMessage);
      return;
    }

    const previousAttempt = readPendingPayment();
    if (
      previousAttempt?.status === 'finalizing'
      || previousAttempt?.status === 'confirmation_unknown'
    ) {
      setError(PAYMENT_CONFIRMATION_UNKNOWN_MESSAGE);
      return;
    }

    processingRef.current = true;
    setProcessing(true);
    paymentConfirmationStore.start();
    setError('');
    let stripeSucceeded = false;

    savePendingPayment({
      createdAt: Date.now(),
      email: normalizedEmail,
      paymentMethod: 'card',
      planId: plan.planId,
      status: 'awaiting_stripe',
    });

    try {
      const { error: submitError } = await refs.elements.submit();
      if (submitError) {
        throw createPaymentFlowError(submitError);
      }

      const confirmation = await refs.stripe.confirmPayment({
        elements: refs.elements,
        clientSecret: refs.clientSecret,
        confirmParams: {
          return_url: getCleanReturnUrl(),
          payment_method_data: {
            billing_details: {
              email: normalizedEmail,
            },
          },
        },
        redirect: 'if_required',
      });

      if (confirmation.error) {
        throw createPaymentFlowError(confirmation.error);
      }
      if (!confirmation.paymentIntent || confirmation.paymentIntent.status !== 'succeeded') {
        throw new Error('Payment confirmation failed. Please try again.');
      }

      stripeSucceeded = true;
      await finishSubscription(
        confirmation.paymentIntent,
        normalizedEmail,
        plan.planId,
        'card',
      );
    }
    catch (paymentError) {
      if (!stripeSucceeded) {
        clearPendingPayment();
      }
      const message = getCheckoutErrorMessage(paymentError);
      if (mountedRef.current) {
        setError(message);
      }
    }
    finally {
      paymentConfirmationStore.stopUnlessNavigating();
      processingRef.current = false;
      if (mountedRef.current) {
        setProcessing(false);
      }
    }
  }, [finishSubscription, plan]);

  const refreshCheckoutSession = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }

    processingRef.current = false;
    applePayFlowRef.current = null;
    setProcessing(false);
    setCardReady(false);
    setCardComplete(false);
    setApplePayAvailable(false);
    setError('');
    setRetryKey((current) => current + 1);
  }, []);

  const startApplePay = useCallback((fallbackEmail = ''): Promise<void> => {
    if (applePayFlowRef.current) {
      return applePayFlowRef.current;
    }

    const paymentRequest = paymentRequestRef.current;
    const refs = checkoutRefs.current;
    if (!paymentRequest || !refs || !plan) {
      const message = 'Apple Pay is still loading. Please try again.';
      setError(message);
      return Promise.reject(new Error(message));
    }

    const flow = new Promise<void>((resolve, reject) => {
      let settled = false;
      let paymentStarted = false;

      const cleanup = () => {
        paymentRequest.off('paymentmethod', handlePaymentMethod);
        paymentRequest.off('cancel', handleCancel);
      };

      const resolveOnce = () => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve();
      };

      const rejectOnce = (paymentError: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        reject(paymentError);
        window.setTimeout(refreshCheckoutSession, 0);
      };

      const handleCancel = () => {
        if (!paymentStarted) {
          resolveOnce();
          window.setTimeout(refreshCheckoutSession, 0);
        }
      };

      const handlePaymentMethod = async (event: PaymentRequestPaymentMethodEvent) => {
        paymentStarted = true;
        paymentRequest.off('cancel', handleCancel);

        let eventCompleted = false;
        const completeEvent = (status: 'success' | 'fail') => {
          if (!eventCompleted) {
            eventCompleted = true;
            event.complete(status);
          }
        };

        if (processingRef.current) {
          const message = 'Another payment is already being processed.';
          completeEvent('fail');
          setError(message);
          rejectOnce(new Error(message));
          return;
        }

        const attParams = getStoredAttParams();
        const normalizedEmail = resolveAttributionEmail(
          attParams,
          event.payerEmail,
        ) ?? resolveAttributionEmail(null, fallbackEmail);
        if (!normalizedEmail) {
          const message = 'Apple Pay did not provide a valid email address.';
          completeEvent('fail');
          setError(message);
          rejectOnce(new Error(message));
          return;
        }

        processingRef.current = true;
        setProcessing(true);
        setError('');
        paymentConfirmationStore.start();
        let stripeSucceeded = false;

        savePendingPayment({
          createdAt: Date.now(),
          email: normalizedEmail,
          paymentMethod: 'apple_pay',
          planId: plan.planId,
          status: 'awaiting_stripe',
        });

        try {
          const initialConfirmation = await refs.stripe.confirmCardPayment(
            refs.clientSecret,
            { payment_method: event.paymentMethod.id },
            { handleActions: false },
          );

          if (initialConfirmation.error || !initialConfirmation.paymentIntent) {
            throw createPaymentFlowError(
              initialConfirmation.error ?? new Error('Apple Pay confirmation failed.'),
            );
          }

          completeEvent('success');
          let confirmedIntent = initialConfirmation.paymentIntent;

          if (confirmedIntent.status === 'requires_action') {
            const actionConfirmation = await refs.stripe.confirmCardPayment(refs.clientSecret);
            if (actionConfirmation.error || !actionConfirmation.paymentIntent) {
              throw createPaymentFlowError(
                actionConfirmation.error ?? new Error('Apple Pay authentication failed.'),
              );
            }
            confirmedIntent = actionConfirmation.paymentIntent;
          }

          if (confirmedIntent.status !== 'succeeded') {
            throw new Error('Apple Pay payment was not completed. Please try again.');
          }

          stripeSucceeded = true;
          await finishSubscription(
            confirmedIntent,
            normalizedEmail,
            plan.planId,
            'apple_pay',
          );
          resolveOnce();
        }
        catch (paymentError) {
          completeEvent('fail');
          if (!stripeSucceeded) {
            clearPendingPayment();
          }
          const message = getCheckoutErrorMessage(paymentError);
          if (mountedRef.current) {
            setError(message);
          }
          rejectOnce(new Error(message));
        }
        finally {
          paymentConfirmationStore.stopUnlessNavigating();
          processingRef.current = false;
          if (mountedRef.current) {
            setProcessing(false);
          }
        }
      };

      paymentRequest.once('paymentmethod', handlePaymentMethod);
      paymentRequest.once('cancel', handleCancel);

      try {
        paymentRequest.show();
      }
      catch (showError) {
        const message = getCheckoutErrorMessage(showError);
        setError(message);
        rejectOnce(new Error(message));
      }
    });

    applePayFlowRef.current = flow;
    const clearFlow = () => {
      if (applePayFlowRef.current === flow) {
        applePayFlowRef.current = null;
      }
    };
    flow.then(clearFlow, clearFlow);

    return flow;
  }, [finishSubscription, plan, refreshCheckoutSession]);

  useEffect(() => {
    if (!enabled || hasStripeReturnParams()) {
      return undefined;
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        refreshCheckoutSession();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [enabled, refreshCheckoutSession]);

  useEffect(() => {
    if (!enabled || !hasStripeReturnParams() || recoveryStartedRef.current) {
      return undefined;
    }

    recoveryStartedRef.current = true;
    let cancelled = false;
    processingRef.current = true;
    setProcessing(true);
    setRecovering(true);
    setError('');

    const recoverPayment = async () => {
      try {
        const pendingPayment = readPendingPayment();
        if (!pendingPayment) {
          throw new Error(
            'We could not restore this payment session. Please contact support before trying again.',
          );
        }
        if (pendingPayment.status !== 'awaiting_stripe') {
          throw new Error(PAYMENT_CONFIRMATION_UNKNOWN_MESSAGE);
        }

        const clientSecret = new URLSearchParams(window.location.search)
          .get('payment_intent_client_secret');
        if (!clientSecret) {
          throw new Error('Payment return data is incomplete. Please contact support.');
        }

        const stripe = await loadStripe(config.stripePublishableKey);
        if (!stripe) {
          throw new Error('Stripe not configured');
        }

        const { paymentIntent, error: retrieveError } = await stripe.retrievePaymentIntent(clientSecret);
        if (retrieveError) {
          throw createPaymentFlowError(retrieveError);
        }
        if (!paymentIntent) {
          throw new Error('Payment status could not be verified. Please contact support.');
        }

        if (paymentIntent.status === 'succeeded') {
          await finishSubscription(
            paymentIntent,
            pendingPayment.email,
            pendingPayment.planId,
            pendingPayment.paymentMethod ?? 'card',
          );
          return;
        }

        if (
          paymentIntent.status === 'requires_payment_method'
          || paymentIntent.status === 'canceled'
        ) {
          clearPendingPayment();
          window.location.replace(getCleanReturnUrl());
          return;
        }

        throw new Error(
          'Your payment is still being verified. Please contact support before trying again.',
        );
      }
      catch (recoveryError) {
        if (!cancelled && mountedRef.current) {
          setError(getCheckoutErrorMessage(recoveryError));
        }
      }
      finally {
        processingRef.current = false;
        if (!cancelled && mountedRef.current) {
          setProcessing(false);
          setRecovering(false);
        }
      }
    };

    void recoverPayment();
    return () => {
      cancelled = true;
    };
  }, [enabled, finishSubscription]);

  useEffect(() => {
    if (
      !enabled
      || hasStripeReturnParams()
      || !plan
      || !paymentElementContainer
    ) {
      return undefined;
    }

    let cancelled = false;
    setInitializing(true);
    setCardReady(false);
    setCardComplete(false);
    setApplePayAvailable(false);
    setError('');

    const handlePaymentElementFocus = () => {
      if (!cancelled) {
        onPaymentElementFocus?.();
      }
    };

    const initialize = async () => {
      try {
        const stripe = await loadStripe(config.stripePublishableKey);
        if (!stripe) {
          throw new Error('Stripe not configured');
        }

        let availablePaymentRequest: PaymentRequest | null = null;
        const firstCharge = plan.trial?.firstCharge
          ?? plan.discount?.discountedPrice
          ?? plan.price;
        const hasValidFirstCharge = Number.isFinite(Number.parseFloat(firstCharge));
        const amount = toMinorUnits(firstCharge, plan.currency);

        if (hasValidFirstCharge) {
          try {
            const paymentRequest = stripe.paymentRequest({
              country: 'US',
              currency: plan.currency.toLowerCase(),
              total: {
                label: 'Trial Verification',
                amount,
              },
              requestPayerName: true,
              requestPayerEmail: true,
            });
            const availability = await paymentRequest.canMakePayment();
            if (availability?.applePay) {
              availablePaymentRequest = paymentRequest;
            }
          }
          catch {
            // Card checkout remains available when the browser cannot initialize Apple Pay.
          }
        }

        const initResponse = await apiStore.initSubscription({ planId: plan.planId });
        if (!initResponse.clientToken) {
          throw new Error('Payment initialization failed: no client token received.');
        }
        if (cancelled) {
          return;
        }

        const elements = stripe.elements({
          clientSecret: initResponse.clientToken,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#3156ff',
              colorText: '#17172d',
              colorDanger: '#ff3b30',
              colorBackground: '#ffffff',
              borderRadius: '12px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif',
              fontSizeBase: '16px',
              spacingUnit: '4px',
            },
          },
        });

        checkoutRefs.current = {
          stripe,
          elements,
          clientSecret: initResponse.clientToken,
        };
        paymentRequestRef.current = availablePaymentRequest;
        setApplePayAvailable(Boolean(availablePaymentRequest));

        const paymentElement = elements.create('payment', {
          layout: {
            type: 'accordion',
            defaultCollapsed: false,
            radios: false,
            spacedAccordionItems: false,
          },
          paymentMethodOrder: ['card'],
          fields: {
            billingDetails: {
              name: 'auto',
              email: 'never',
              address: 'if_required',
            },
          },
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: 'never',
          },
        });

        paymentElementRef.current = paymentElement;

        paymentElement.on('ready', () => {
          if (!cancelled) {
            setCardReady(true);
          }
        });
        paymentElement.on('change', (event) => {
          if (!cancelled) {
            setCardComplete(event.complete);
            setError('');
          }
        });
        paymentElement.on('focus', handlePaymentElementFocus);
        paymentElement.on('loaderror', (event) => {
          if (!cancelled) {
            setError(getErrorMessage(event.error));
          }
        });

        paymentElement.mount(paymentElementContainer);
      }
      catch (initializationError) {
        if (!cancelled) {
          setApplePayAvailable(false);
          setError(getErrorMessage(initializationError));
        }
      }
      finally {
        if (!cancelled) {
          setInitializing(false);
        }
      }
    };

    void initialize();

    return () => {
      cancelled = true;
      paymentRequestRef.current?.off('paymentmethod');
      paymentRequestRef.current?.off('cancel');
      paymentElementRef.current?.off('focus', handlePaymentElementFocus);
      paymentElementRef.current?.unmount();
      paymentRequestRef.current = null;
      paymentElementRef.current = null;
      checkoutRefs.current = null;
    };
  }, [
    confirmStripePayment,
    enabled,
    onPaymentElementFocus,
    paymentElementContainer,
    plan,
    retryKey,
  ]);

  const submitCard = useCallback((email: string) => {
    return confirmStripePayment(email);
  }, [confirmStripePayment]);

  const retryInitialization = useCallback(() => {
    refreshCheckoutSession();
  }, [refreshCheckoutSession]);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    initializing,
    processing,
    cardComplete,
    cardReady,
    applePayAvailable,
    error,
    recovering,
    startApplePay,
    submitCard,
    retryInitialization,
    clearError,
  };
}
